import type { Vec3 } from "./math.js";

/**
 * Escenas planas (un plano, una obra, un documento) dentro del modo inmersivo.
 *
 * Una escena plana no rodea a quien mira: es una imagen. Antes el modo VR la
 * trataba como una panorámica, no encontraba esfera que pintar y fallaba el
 * salto: seguía viéndose la escena anterior con los hotspots de la plana
 * (guardados en coordenadas de la imagen) repartidos por el espacio. Y si se
 * entraba en VR estando en ella, la sesión arrancaba sin un solo fotograma.
 *
 * Ahora se pinta como una pantalla grande delante, a la distancia de los
 * hotspots, con los marcadores encima de la imagen en su sitio.
 */

/** Distancia de la pantalla (la misma que la de los hotspots de las panorámicas). */
export const FLAT_DISTANCE = 6;
/** Alto de la pantalla a esa distancia: unos 45° de campo vertical. */
const FLAT_HEIGHT = 5;
/** Ancho máximo: unos 80° de campo horizontal, para las imágenes apaisadas. */
const FLAT_MAX_WIDTH = 10;

/** Tamaño en metros de la pantalla para una imagen de proporción `aspect` (ancho / alto). */
export function flatPlaneSize(aspect: number): { width: number; height: number } {
  const a = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  let height = FLAT_HEIGHT;
  let width = height * a;
  if (width > FLAT_MAX_WIDTH) {
    width = FLAT_MAX_WIDTH;
    height = width / a;
  }
  return { width, height };
}

/**
 * Punto de la pantalla en el marco de la escena (antes del desfase de yaw y
 * de la posición de la cabeza): la pantalla mira hacia +Z desde −Z, y (x, y)
 * van de 0 a 1 desde la esquina superior izquierda de la imagen. `lift` lo
 * adelanta hacia quien mira (los marcadores van un poco por delante).
 */
export function flatPoint(x: number, y: number, size: { width: number; height: number }, lift = 0): Vec3 {
  return [(x - 0.5) * size.width, (0.5 - y) * size.height, -(FLAT_DISTANCE - lift)];
}

/** Matriz de modelo del cuadrilátero unidad sobre la pantalla (marco de la escena). */
export function flatModelMatrix(size: { width: number; height: number }): Float32Array {
  return new Float32Array([size.width, 0, 0, 0, 0, size.height, 0, 0, 0, 0, 1, 0, 0, 0, -FLAT_DISTANCE, 1]);
}

/** Aplica una matriz de modelo (columnas, como WebGL) a un punto. */
export function transformPoint(m: Float32Array, p: Vec3): Vec3 {
  return [
    m[0]! * p[0] + m[4]! * p[1] + m[8]! * p[2] + m[12]!,
    m[1]! * p[0] + m[5]! * p[1] + m[9]! * p[2] + m[13]!,
    m[2]! * p[0] + m[6]! * p[1] + m[10]! * p[2] + m[14]!,
  ];
}
